import { Fragment, type ReactNode } from 'react'
import { Link } from 'react-router'
import { Inbox, ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react'
import { Checkbox } from './Checkbox'
import { Combobox } from './Combobox'

export interface DataTableColumn<T> {
  key: string
  label: string
  render: (row: T) => ReactNode
  /** Override per la card mobile quando il render tabellare non si presta (es. classi text-end/colSpan). */
  cardRender?: (row: T) => ReactNode
  /** Cosa mostrare nella card mobile: 'title' = intestazione, 'primary' = subito visibile, 'secondary' (default) = riga label:valore, 'desktop-only' = solo tabella. */
  priority?: 'title' | 'primary' | 'secondary' | 'desktop-only'
  sortable?: boolean
  className?: string
}

export interface DataTableSort {
  column: string
  direction: 'asc' | 'desc'
  onSort: (column: string) => void
}

export interface DataTableSelection {
  selectedIds: Set<string>
  onToggleRow: (id: string) => void
  onToggleAll: (checked: boolean) => void
}

export interface DataTablePagination {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
}

interface ResponsiveDataTableProps<T> {
  columns: DataTableColumn<T>[]
  data: T[]
  getRowId: (row: T) => string
  isLoading?: boolean
  emptyIcon?: ReactNode
  emptyMessage?: string
  emptyActionLabel?: string
  emptyActionTo?: string
  renderActions?: (row: T) => ReactNode
  /** Dettaglio espandibile mostrato subito sotto la riga o la card corrispondente. */
  renderExpanded?: (row: T) => ReactNode | null
  selection?: DataTableSelection
  sort?: DataTableSort
  pagination?: DataTablePagination
  rowClassName?: (row: T) => string
  /** Override puntuale per forzare una vista indipendentemente dal viewport (uso raro, es. compatibilità transitoria). */
  forceView?: 'table' | 'card'
}

function buildPageWindow(page: number, totalPages: number) {
  const windowSize = Math.min(totalPages, 7)
  return Array.from({ length: windowSize }, (_, i) => {
    if (totalPages <= 7) return i + 1
    if (page <= 4) return i + 1
    if (page >= totalPages - 3) return totalPages - 6 + i
    return page - 3 + i
  })
}

/**
 * Tabella dati responsive: un'unica definizione di colonne alimenta sia la <table> desktop
 * (>=768px) sia la lista a card impilate su mobile. Sostituisce DataTable + ogni tabella
 * scritta a mano nelle pagine Index dell'app (vedi piano design system).
 */
export function ResponsiveDataTable<T>({
  columns,
  data,
  getRowId,
  isLoading,
  emptyIcon,
  emptyMessage,
  emptyActionLabel,
  emptyActionTo,
  renderActions,
  renderExpanded,
  selection,
  sort,
  pagination,
  rowClassName,
  forceView,
}: ResponsiveDataTableProps<T>) {
  const isEmpty = !isLoading && data.length === 0
  const sortableColumns = columns.filter((c) => c.sortable)

  const tableWrapperClass = forceView === 'card' ? 'hidden' : forceView === 'table' ? 'block' : 'da-table-desktop hidden md:block'
  const cardWrapperClass = forceView === 'table' ? 'hidden' : forceView === 'card' ? 'block' : 'da-table-cards block md:hidden'

  return (
    <div data-testid="responsive-data-table" className="da-responsive-data-table rounded-xl border border-border bg-canvas shadow-sm">
      {isLoading && (
        <div className="flex justify-center py-12">
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"
            role="status"
            aria-label="Caricamento..."
          />
        </div>
      )}

      {isEmpty && (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-muted">
          {emptyIcon ?? <Inbox size={40} />}
          <p className="m-0">
            {emptyMessage || 'Nessun dato trovato'}
            {emptyActionLabel && emptyActionTo && (
              <>
                .{' '}
                <Link to={emptyActionTo} className="text-link underline">
                  {emptyActionLabel}
                </Link>
              </>
            )}
          </p>
        </div>
      )}

      {!isLoading && !isEmpty && (
        <>
          <div className={`overflow-x-auto ${tableWrapperClass}`}>
            <table className="w-full text-left text-sm">
              <thead className="bg-surface text-muted">
                <tr>
                  {selection && (
                    <th className="w-10 px-3 py-2">
                      <Checkbox
                        aria-label="Seleziona tutti"
                        checked={data.length > 0 && data.every((row) => selection.selectedIds.has(getRowId(row)))}
                        onChange={(e) => selection.onToggleAll(e.target.checked)}
                      />
                    </th>
                  )}
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={`px-3 py-3 text-[10px] font-semibold tracking-[0.08em] text-muted uppercase ${col.sortable ? 'cursor-pointer select-none' : ''} ${col.className ?? ''}`}
                      onClick={col.sortable && sort ? () => sort.onSort(col.key) : undefined}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {sort &&
                          sort.column === col.key &&
                          (sort.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </span>
                    </th>
                  ))}
                  {renderActions && <th className="px-3 py-3 text-right text-[10px] font-semibold tracking-[0.08em] text-muted uppercase">Azioni</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {data.map((row) => {
                  const id = getRowId(row)
                  const expandedContent = renderExpanded?.(row)
                  return (
                    <Fragment key={id}>
                      <tr className={`hover:bg-surface ${rowClassName?.(row) ?? ''}`}>
                        {selection && (
                          <td className="px-3 py-2">
                            <Checkbox
                              aria-label="Seleziona riga"
                              checked={selection.selectedIds.has(id)}
                              onChange={() => selection.onToggleRow(id)}
                            />
                          </td>
                        )}
                        {columns.map((col) => (
                          <td key={col.key} className={`px-3 py-3 align-middle ${col.className ?? ''}`}>
                            {col.render(row)}
                          </td>
                        ))}
                        {renderActions && (
                          <td className="px-3 py-3 text-right">
                            <div className="inline-flex items-center gap-1">{renderActions(row)}</div>
                          </td>
                        )}
                      </tr>
                      {expandedContent && (
                        <tr>
                          <td colSpan={columns.length + (selection ? 1 : 0) + (renderActions ? 1 : 0)} className="border-t border-border-subtle bg-surface p-0">
                            <div className="px-4 py-3">{expandedContent}</div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className={`${cardWrapperClass} divide-y divide-border-subtle`}>
            {sort && sortableColumns.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-2">
                <div className="flex-1">
                  <Combobox
                    value={sort.column}
                    onChange={sort.onSort}
                    options={sortableColumns.map((col) => ({ value: col.key, label: `Ordina per ${col.label}` }))}
                  />
                </div>
                <button
                  type="button"
                  className="cursor-pointer rounded-md border border-border-strong p-1.5"
                  onClick={() => sort.onSort(sort.column)}
                  aria-label="Inverti ordine"
                >
                  {sort.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>
            )}
            {data.map((row) => {
              const id = getRowId(row)
              const titleCol = columns.find((c) => c.priority === 'title')
              const primaryCols = columns.filter((c) => c.priority === 'primary')
              const secondaryCols = columns.filter((c) => !c.priority || c.priority === 'secondary')
              const expandedContent = renderExpanded?.(row)
              return (
                <div key={id} className={`list-card p-3 ${rowClassName?.(row) ?? ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    {selection && (
                      <span
                        className="-m-3 flex h-11 w-11 shrink-0 items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="relative inline-flex h-5 w-5 items-center justify-center">
                          <input
                            type="checkbox"
                            aria-label="Seleziona riga"
                            className="peer h-5 w-5 shrink-0 appearance-none rounded border border-border-strong bg-canvas checked:border-primary-600 checked:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                            checked={selection.selectedIds.has(id)}
                            onChange={() => selection.onToggleRow(id)}
                          />
                          <svg
                            className="pointer-events-none absolute h-3.5 w-3.5 text-on-dark opacity-0 peer-checked:opacity-100"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={3}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      {titleCol && <div className="font-semibold">{(titleCol.cardRender ?? titleCol.render)(row)}</div>}
                      {primaryCols.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {primaryCols.map((col) => (
                            <div key={col.key}>{(col.cardRender ?? col.render)(row)}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {secondaryCols.length > 0 && (
                    <div className="mt-2 space-y-1 text-sm">
                      {secondaryCols.map((col) => (
                        <div key={col.key} className="flex items-center justify-between gap-2">
                          <span className="shrink-0 text-muted">{col.label}</span>
                          <span className="min-w-0 text-right wrap-break-word">{(col.cardRender ?? col.render)(row)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {renderActions && (
                    <div className="mt-3 flex justify-end gap-2 border-t border-border-subtle pt-2">
                      {renderActions(row)}
                    </div>
                  )}
                  {expandedContent && <div className="mt-3 border-t border-border-subtle pt-3">{expandedContent}</div>}
                </div>
              )
            })}
          </div>
        </>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="border-t border-border px-3 py-3">
          <nav className="flex justify-center">
            <ul className="flex items-center gap-1">
              <li>
                <button
                  aria-label="Pagina precedente"
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-border disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => pagination.onPageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                >
                  <ChevronLeft size={16} />
                </button>
              </li>
              {buildPageWindow(pagination.page, pagination.totalPages).map((pageNum) => (
                <li key={pageNum}>
                  <button
                    className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border text-sm ${
                      pagination.page === pageNum
                        ? 'border-primary-500 bg-primary-500 text-on-dark'
                        : 'border-border hover:bg-surface'
                    }`}
                    onClick={() => pagination.onPageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                </li>
              ))}
              <li>
                <button
                  aria-label="Pagina successiva"
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-border disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => pagination.onPageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  <ChevronRight size={16} />
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  )
}
